import { useState, useEffect } from "react";
import { apiClient } from "../../App";
import { FileText, Download, Eye, Upload, RefreshCw, CheckCircle2, AlertCircle, Loader2, Link2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="w-6 h-6 animate-spin" />
    </div>
  );
}

function EmptyAgreement() {
  return (
    <div className="text-center py-8">
      <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
      <p className="text-sm font-medium mb-1">No Agreement Found</p>
      <p className="text-xs text-muted-foreground mb-4">Agreement was not attached in intake</p>
      <Button variant="outline" size="sm">
        <Upload className="w-3.5 h-3.5 mr-1" /> Upload Agreement
      </Button>
    </div>
  );
}

function UploadedBadge() {
  return (
    <Badge variant="default" className="text-xs">
      <span className="flex items-center gap-1">
        <CheckCircle2 className="w-3 h-3" /> Uploaded
      </span>
    </Badge>
  );
}

function DocusignBadge() {
  return (
    <Badge variant="default" className="text-xs">
      <span className="flex items-center gap-1">
        <CheckCircle2 className="w-3 h-3" /> DocuSign
      </span>
    </Badge>
  );
}

function LinkBadge() {
  return (
    <Badge variant="secondary" className="text-xs">
      <span className="flex items-center gap-1">
        <Link2 className="w-3 h-3" /> Link
      </span>
    </Badge>
  );
}

function ButtonLoader() {
  return <Loader2 className="w-4 h-4 animate-spin" />;
}

function RefreshIcon() {
  return <RefreshCw className="w-4 h-4" />;
}

function SmallButtonLoader() {
  return <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />;
}

function SmallFileIcon() {
  return <FileText className="w-3.5 h-3.5 mr-1" />;
}

export default function ActivationAgreementTab({ 
  program,
  fetchingAgreement,
  parsingAgreement,
  parsedData,
  onFetchAgreement,
  onParseAgreement
}) {
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (program && program.id) {
      loadAgreementData();
    }
  }, [program]);
  
  async function loadAgreementData() {
    setLoading(true);
    try {
      const r = await apiClient.get("/programs/" + program.id + "/agreement");
      setAgreement(r.data);
    } catch (e) {
      const hasLink = program.signed_agreement_link;
      const hasFileId = program.signed_agreement_file_id;
      if (hasLink || hasFileId) {
        setAgreement({
          source: hasFileId ? "upload" : "link",
          original_link_url: program.signed_agreement_link,
          file_id: program.signed_agreement_file_id,
          is_stored: Boolean(hasFileId)
        });
      }
    }
    setLoading(false);
  }
  
  async function openPreview() {
    const link = agreement && agreement.original_link_url;
    const fileId = agreement && agreement.file_id;
    
    if (link) {
      window.open(link, "_blank");
      return;
    }
    if (fileId) {
      try {
        const response = await apiClient.get("/programs/" + program.id + "/agreement/download", {
          responseType: 'blob',
          // Same URL after a replace — bust any copy the browser cached of the old PDF.
          params: { _: Date.now() },
        });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
      } catch (e) {
        console.error("Failed to preview agreement:", e);
      }
    }
  }
  
  async function openDownload() {
    const link = agreement && agreement.original_link_url;
    const fileId = agreement && agreement.file_id;
    
    if (link) {
      window.open(link, "_blank");
      return;
    }
    if (fileId) {
      try {
        const response = await apiClient.get("/programs/" + program.id + "/agreement/download", {
          responseType: 'blob',
          // Same URL after a replace — bust any copy the browser cached of the old PDF.
          params: { _: Date.now() },
        });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = (program.program_name || 'agreement') + '.pdf';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (e) {
        console.error("Failed to download agreement:", e);
      }
    }
  }
  
  if (loading) {
    return <LoadingSpinner />;
  }

  const hasAgreement = Boolean(agreement) || Boolean(program && program.signed_agreement_link) || Boolean(program && program.signed_agreement_file_id);
  
  if (!hasAgreement) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Signed Agreement
            </CardTitle>
            <CardDescription>Service agreement document</CardDescription>
          </CardHeader>
          <CardContent>
            <EmptyAgreement />
          </CardContent>
        </Card>
      </div>
    );
  }
  
  const isUploaded = Boolean(agreement && agreement.source === "upload") || Boolean(agreement && agreement.file_id) || Boolean(program && program.agreement_type === "upload");
  const agreementLink = (agreement && agreement.original_link_url) || (program && program.signed_agreement_link);
  // Quote sent through DocuSign but not signed (or not reachable) at intake —
  // Fetch pulls the signed PDF straight from the envelope.
  const fromDocusign = Boolean(agreement && agreement.source === "docusign");
  const docusignPending = fromDocusign && !(agreement && agreement.is_stored);
  const showFetchSection = !isUploaded && (Boolean(agreementLink) || docusignPending);
  const showParseSection = isUploaded || Boolean(agreement && agreement.is_stored);
  const programName = program && program.program_name;
  const hasParsedData = Boolean(parsedData);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Signed Agreement
          </CardTitle>
          <CardDescription>Service agreement document</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-start justify-between p-4 bg-secondary/30 rounded-lg">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-muted-foreground" />
                  <span className="font-medium">{programName} Agreement</span>
                </div>
                <div className="flex gap-2">
                  {fromDocusign ? <DocusignBadge /> : isUploaded ? <UploadedBadge /> : <LinkBadge />}
                </div>
                {agreementLink && !isUploaded && (
                  <p className="text-xs text-muted-foreground truncate max-w-sm">{agreementLink}</p>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <Button variant="outline" size="sm" onClick={openPreview} className="h-8">
                  <Eye className="w-3.5 h-3.5 mr-1" /> Preview
                </Button>
                <Button variant="outline" size="sm" onClick={openDownload} className="h-8">
                  <Download className="w-3.5 h-3.5 mr-1" /> Download
                </Button>
              </div>
            </div>
            
            {showFetchSection && (
              <div className="p-4 bg-orange-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{docusignPending ? "Pull signed PDF from DocuSign" : "Fetch PDF to store locally"}</p>
                    <p className="text-xs text-muted-foreground">{docusignPending ? "Works once the client has finished signing" : "Required for parsing"}</p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={onFetchAgreement} 
                    disabled={fetchingAgreement}
                  >
                    {fetchingAgreement ? <ButtonLoader /> : <RefreshIcon />}
                  </Button>
                </div>
              </div>
            )}
            
            {showParseSection && (
              <div className="flex items-center justify-between p-3 bg-secondary/20 rounded-lg">
                <div>
                  <p className="text-sm font-medium">AI Agreement Parser</p>
                  <p className="text-xs text-muted-foreground">Extract deliverables from PDF</p>
                </div>
                <Button variant="outline" size="sm" onClick={onParseAgreement} disabled={parsingAgreement}>
                  {parsingAgreement ? <SmallButtonLoader /> : <SmallFileIcon />}
                  {hasParsedData ? "Re-Parse" : "Parse"}
                </Button>
              </div>
            )}
            
            {hasParsedData && <ParsedDataDisplay data={parsedData} />}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ParsedDataDisplay({ data }) {
  if (!data) return null;
  
  const deliverables = data.deliverables || [];
  const milestones = data.milestones || [];
  
  return (
    <div className="p-4 bg-green-50 rounded-lg space-y-3">
      <h4 className="text-sm font-medium text-green-800">Extracted Information</h4>
      <ParsedFieldsGrid data={data} />
      {deliverables.length > 0 && <DeliverablesSection items={deliverables} />}
      {milestones.length > 0 && <MilestonesSection items={milestones} />}
    </div>
  );
}

function ParsedFieldsGrid({ data }) {
  return (
    <div className="grid grid-cols-2 gap-2 text-sm">
      {data.client_name && <ParsedField label="Client" value={data.client_name} />}
      {data.contract_value && <ParsedField label="Value" value={"$" + Number(data.contract_value).toLocaleString()} />}
      {data.timeline_text && <ParsedField label="Timeline" value={data.timeline_text} />}
      {data.payment_terms && <ParsedField label="Payment" value={data.payment_terms} />}
    </div>
  );
}

function ParsedField({ label, value }) {
  return (
    <p>
      <span className="text-muted-foreground">{label}:</span> {value}
    </p>
  );
}

function DeliverablesSection({ items }) {
  const displayed = items.slice(0, 8);
  const remaining = items.length - 8;
  
  return (
    <div className="mt-3 pt-3 border-t border-green-200">
      <p className="text-xs font-medium text-green-800 mb-2">Deliverables ({items.length})</p>
      <div className="flex flex-wrap gap-1.5">
        {displayed.map((d, i) => (
          <DeliverableBadge key={i} item={d} />
        ))}
        {remaining > 0 && <Badge variant="outline" className="text-xs">+{remaining} more</Badge>}
      </div>
    </div>
  );
}

function DeliverableBadge({ item }) {
  const qty = item.quantity ? item.quantity + "x " : "";
  const type = item.type || item.deliverable_type || String(item);
  return (
    <Badge variant="secondary" className="text-xs bg-white">
      {qty}{type}
    </Badge>
  );
}

function MilestonesSection({ items }) {
  const names = items.map(m => m.name).join(", ");
  return (
    <div className="mt-2 pt-2 border-t border-green-200">
      <p className="text-xs font-medium text-green-800 mb-1">Milestones ({items.length})</p>
      <p className="text-xs text-muted-foreground">{names}</p>
    </div>
  );
}
